import React, { useMemo } from 'react';
import { Autocomplete, Checkbox, TextField } from '@mui/material';
import Chip from '@mui/material/Chip';
import { theme } from '../../../theme';
import { ALL_STORES_TOKEN, MultiStorePickerProps, StoreOption } from './types';

const MultiStorePicker: React.FC<MultiStorePickerProps> = ({
  stores,
  value,
  onChange,
  allStoresLabel,
  label = 'Store',
  placeholder = 'Search',
  disabled = false,
  size = 'small',
}) => {
  const storeOptions = useMemo<StoreOption[]>(() => {
    const raw: StoreOption[] = (stores || [])
      .map(s => ({ storeName: s.storeName ?? '', storeNumber: s.storeNumber ?? '' }))
      .filter(s => s.storeName);
    const numeric: StoreOption[] = [];
    const alpha: StoreOption[] = [];
    raw.forEach(o => {
      const m = o.storeName.match(/^\d+/);
      if (m) {
        o._numKey = parseInt(m[0], 10);
        numeric.push(o);
      } else {
        alpha.push(o);
      }
    });
    numeric.sort((a, b) =>
      (a._numKey ?? 0) === (b._numKey ?? 0)
        ? a.storeName.localeCompare(b.storeName)
        : (a._numKey ?? 0) - (b._numKey ?? 0),
    );
    alpha.sort((a, b) => a.storeName.localeCompare(b.storeName));
    return [
      { storeName: allStoresLabel || 'All Stores', storeNumber: ALL_STORES_TOKEN, isAll: true },
      ...numeric,
      ...alpha,
    ];
  }, [stores, allStoresLabel]);

  const allStoresSelected = value.mode === 'all';

  const selectedStoreChips: StoreOption[] = allStoresSelected
    ? [{ storeName: allStoresLabel || 'All Stores', storeNumber: ALL_STORES_TOKEN, isAll: true }]
    : value.mode === 'specific'
      ? value.storeNumbers.map(sn => {
        const found = stores?.find(s => s.storeNumber === sn);
        return { storeName: found?.storeName || sn, storeNumber: sn };
      })
      : [];

  const handleStoreAutocompleteChange = (_: unknown, newValue: StoreOption[]) => {
    const selected = newValue || [];
    const pickedAll = selected.some(o => o.isAll);
    const specifics = selected.filter(o => !o.isAll);

    // No selection => empty specific (so user can select stores)
    if (selected.length === 0) {
      onChange({ mode: 'specific', storeNumbers: [] });
      return;
    }

    // Only All selected
    if (pickedAll && specifics.length === 0) {
      onChange({ mode: 'all' });
      return;
    }

    // User clicked All while already in specific mode (pickedAll plus some specifics) => revert to All
    if (pickedAll && specifics.length > 0 && value.mode === 'specific') {
      onChange({ mode: 'all' });
      return;
    }

    // Mixed while previously All OR just specifics: ignore All and keep specifics
    const uniqueNumbers = Array.from(new Set(specifics.map(s => s.storeNumber)));
    onChange({ mode: 'specific', storeNumbers: uniqueNumbers });
  };

  return (
    <Autocomplete<StoreOption, true, false, false>
      multiple
      disableCloseOnSelect
      size={size}
      disabled={disabled}
      options={storeOptions}
      getOptionLabel={(option) => option.storeName}
      isOptionEqualToValue={(option, val) => option.storeNumber === val.storeNumber}
      value={selectedStoreChips}
      onChange={handleStoreAutocompleteChange}
      renderTags={(tagValue, getTagProps) =>
        tagValue.map((option, index) => {
          const { key: _ignoreKey, ...chipProps } = getTagProps({ index });
          void _ignoreKey;
          const isAll = option.storeNumber === ALL_STORES_TOKEN;
          return (
            <Chip
              key={option.isAll ? 'ALL_STORES' : option.storeNumber || index}
              {...chipProps}
              label={option.storeName}
              color={isAll ? 'primary' : undefined}
              sx={isAll ? { backgroundColor: theme.palette.primary.main, color: '#fff' } : undefined}
            />
          );
        })
      }
      renderInput={(params) => (
        <TextField
          {...params}
          label={label}
          placeholder={placeholder}
        />
      )}
      renderOption={(props, option) => (
        <li
          {...props}
          key={option.storeNumber}
        >
          <Checkbox
            size="small"
            checked={
              option.isAll
                ? allStoresSelected
                : value.mode === 'specific' && value.storeNumbers.includes(option.storeNumber)
            }
            tabIndex={-1}
            disableRipple
            style={{ marginRight: 8 }}
          />
          {option.storeName}
        </li>
      )}
    />
  );
};

export default MultiStorePicker;
